import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { apiClient } from "../../services/apiClient";
import { RentalObjectBundle } from "../../types/api-types";

interface EditBundleArticleModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bundle: RentalObjectBundle | null;
  objectId: string;
}

interface BundleEditForm {
  bundlePrice: string;
  isPublic: boolean;
  notes: string;
}

const EditBundleArticleModal: React.FC<EditBundleArticleModalProps> = ({
  open,
  onClose,
  onSuccess,
  bundle,
  objectId,
}) => {
  const [formData, setFormData] = useState<BundleEditForm>({
    bundlePrice: "",
    isPublic: false,
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [originalNotes, setOriginalNotes] = useState<string>("");

  // Initialize form data when bundle changes
  useEffect(() => {
    if (bundle) {
      const notes = bundle.notes || "";
      setFormData({
        bundlePrice: bundle.bundlePrice?.toString() || "",
        isPublic: bundle.isPublic ?? false,
        notes: notes,
      });
      setOriginalNotes(notes);
    }
  }, [bundle]);

  const handleInputChange =
    (field: keyof BundleEditForm) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const validateForm = (): boolean => {
    if (!formData.bundlePrice || parseFloat(formData.bundlePrice) < 0) {
      setError("Gültiger Bundle-Preis ist erforderlich");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError(null);

    if (!validateForm() || !bundle) {
      return;
    }

    try {
      setLoading(true);

      const updateData: any = {
        bundlePrice: parseFloat(formData.bundlePrice),
        isActive: bundle.isActive, // Keep original isActive
        isPublic: formData.isPublic,
      };

      // Only send notes if they have changed
      if (formData.notes !== originalNotes) {
        updateData.notes = formData.notes || null;
      }

      await apiClient.updateBundleArticle(objectId, bundle.id!, updateData);

      setLoading(false);
      onSuccess();
      onClose();
    } catch (error: any) {
      setLoading(false);
      setError(`Fehler beim Aktualisieren: ${error.message}`);
    }
  };

  const handleClose = () => {
    setError(null);
    setFormData({
      bundlePrice: "",
      isPublic: false,
      notes: "",
    });
    onClose();
  };

  if (!bundle) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          Bundle-Artikel bearbeiten
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: "grey.500",
            "&:hover": { color: "grey.700" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Artikel: {bundle.article?.name || "Unbekannter Artikel"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Bundle-Preis (€)"
            type="number"
            value={formData.bundlePrice}
            onChange={handleInputChange("bundlePrice")}
            fullWidth
            required
            inputProps={{ min: 0, step: 0.01 }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isPublic}
                onChange={handleInputChange("isPublic")}
                color="primary"
              />
            }
            label="öffentlich sichtbar"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "0.9rem",
                fontWeight: 500,
              },
            }}
          />

          <TextField
            label="Notizen"
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleInputChange("notes")}
            fullWidth
            placeholder="Optionale Notizen zum Bundle..."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: "none",
            fontWeight: 600,
            minWidth: 100,
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Aktualisieren"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBundleArticleModal;
